import React, { useState, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { CircularProgress, Backdrop } from "@mui/material";
import { CSVLink } from "react-csv";
import { mbxUserAtom } from "../atoms/atoms";
import { graphql, graphqlOperation } from "../modules/AmplifyServices";
import { execReadBySortkey, execReadByPK } from "../modules/DBService";
import {
    getScheduleCounts,
    getScheduleDatesToReport
} from "../modules/ScheduleService";
import { userByCompany } from "../queries/UserQueries";
import {
    providerByCompany,
    serviceTypeByCompanyCustom
} from "../queries/ProviderFormQueries";
import LargeReportDialog from "../components/LargeReportDialog";
import moment from "moment";

const ExportUsersList = ({ onExportComplete }) => {
    const [mbxUser] = useAtom(mbxUserAtom);
    const limit = process.env.REACT_APP_LISTLIMIT;
    const [exportReadyProviders, setExportReadyProviders] = useState();
    const [exportDownloadReady, setExportDownloadReady] = useState(false);
    const [preparingExportData, setPreparingExportData] = useState(true); // Automatically starts preparation
    const [showDialog, setShowDialog] = useState(false);
    const [longReport, setLongReport] = useState(false);
    const [csvHeaders] = useState([
        { label: "Status", key: "Status" },
        { label: "ID", key: "ID" },
        { label: "First name", key: "First Name" },
        { label: "Last name", key: "Last Name" },
        { label: "Email", key: "Email" },
        { label: "Phone", key: "Phone Number" },
        { label: "Permalink", key: "Permalink" },
        { label: "Role", key: "Role" },
        { label: "Timezone", key: "Timezone" },
        {
            label: "Available for in-home appointments",
            key: "Available For In Home Appointments"
        },
        {
            label: "Max travel time",
            key: "Max Travel Time"
        },
        {
            label: "Available for virtual appointments",
            key: "Available For Virtual Appointments"
        },
        {
            label: "Virtual meeting link",
            key: "Virtual Meeting Link"
        },
        {
            label: "Schedules with future availability",
            key: "Schedules"
        },
        {
            label: "Schedules last updated",
            key: "Schedules Last Updated"
        },
        {
            label: "Send daily agenda email",
            key: "Send Daily Agenda Email"
        },
        { label: "Skills", key: "Skills" },
        { label: "Services", key: "Services" },
        { label: "Bio", key: "Bio" },
        { label: "Last login", key: "Last login" },
        { label: "Created on", key: "Created On" },
        { label: "Updated on", key: "Updated On" }
    ]);
    const csvLinkRef = useRef();

    async function fetchServiceTypes() {
        const result = await graphql(
            graphqlOperation(serviceTypeByCompanyCustom, {
                companyId: mbxUser.companyId,
                limit: process.env.REACT_APP_LISTLIMIT,
                filter: {
                    and: [
                        { deleted: { ne: true } },
                        { active: { ne: false } },
                        { isVisible: { ne: false } }
                    ]
                }
            })
        );
        return result.data.serviceTypeByCompany.items;
    }

    const escapeQuotes = (value) => {
        if (typeof value !== "string") return value;
        return value.replace(/"/g, '""'); //double the quotes
    };

    const csvData = async (providersList) => {
        try {
            const schedules = await getScheduleCounts(mbxUser.companyId);
            const scheduleDatesToReport = await getScheduleDatesToReport(
                mbxUser.companyId,
                schedules.SCs
            );

            providersList.items.sort(
                //sort the providersList is descending order based on created At
                (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
            );

            let rows = [];
            let serviceTypeId,
                serviceTypeList = "",
                skillsList = "",
                i,
                j;
            let newItem;

            let usersByCompany = await execReadBySortkey({
                opname: "userByCompany",
                op: userByCompany,
                id: { companyId: mbxUser.companyId },
                limit: limit
            });

            const companyServices = await fetchServiceTypes();
            if (providersList && providersList.items.length > 0) {
                providersList.items.map(async (item) => {
                    usersByCompany.items.map(async (item1) => {
                        if (item.emailaddress == item1.emailaddress) {
                            const loginInfo = JSON.parse(item1.LoginInfo);
                            item.lastLogin = loginInfo
                                ? moment(loginInfo.lastLoginDate).format(
                                      "YYYY-MM-DD hh:mm A"
                                  )
                                : "";
                            item.contactconsent = item1.contactconsent
                                ? "TRUE"
                                : "FALSE";
                            item.role =
                                item1.role === "COMPANY_ADMIN_PROVIDER"
                                    ? "Admin"
                                    : item1.role === "MANAGER_PROVIDER"
                                      ? "Manager"
                                      : "Provider";
                            item.maxTravelTime = item1.provider?.maxTravelTime
                                ? item1.provider.maxTravelTime
                                : "";
                        }
                    });
                    serviceTypeList = "";
                    skillsList = "";
                    for (i = 0; i < item.servicetypes.items.length; i++) {
                        serviceTypeId =
                            item.servicetypes.items[i].servicetype.id;
                        for (j = 0; j < companyServices.length; j++) {
                            if (companyServices[j].id === serviceTypeId) {
                                const std = companyServices[j].name;
                                if (serviceTypeList === "") {
                                    serviceTypeList = std;
                                } else {
                                    serviceTypeList += "; " + std;
                                }
                                newItem = { ...item, serviceTypeList };
                                item = newItem;
                            }
                        }
                        for (let a = 0; a < item.skills.items.length; a++) {
                            const std = item.skills.items[a].skill.name;
                            if (skillsList === "") {
                                skillsList = std;
                            } else if (!skillsList.includes(std)) {
                                skillsList += "; " + std;
                            }
                            newItem = { ...item, skillsList };
                            item = newItem;
                        }
                    }
                    item.numberofschedules = schedules.scheduleCounts[item.id]
                        ? schedules.scheduleCounts[item.id]
                        : 0;
                    item.createdAt = moment(item.createdAt).format(
                        "YYYY-MM-DD hh:mm A"
                    );
                    item.updatedAt = moment(item.updatedAt).format(
                        "YYYY-MM-DD hh:mm A"
                    );
                    item.inhomeservicestatus = item.offersMobileServices
                        ? "TRUE"
                        : "FALSE";
                    item.virtualservicestatus = item.offersVirtualServices
                        ? "TRUE"
                        : "FALSE";
                    item.permalink =
                        mbxUser && mbxUser.company
                            ? `https://${mbxUser.company.subdomain}/provider/${item.permalink}`
                            : "";
                    item.emailaddress = item.emailaddress
                        ? item.emailaddress
                        : "";
                    item.status = item.deleted
                        ? "Deleted"
                        : item.active
                          ? "Active"
                          : "Inactive";
                    item.virtualMeetingLink = item.vmlink ? item.vmlink : "";
                    item.firstname = item.firstname ? item.firstname : "";
                    item.lastname = item.lastname ? item.lastname : "";
                    item.email = item.email ? item.email : "";
                    item.phone = item.phone ? item.phone : "";
                    item.timezone = item.timezone ? item.timezone : "";
                    item.bio = item.bio
                        ? escapeQuotes(item.bio.replace(/\n/g, " "))
                        : "";
                    item.bio = item.bio?.endsWith('"')
                        ? (item.bio = item.bio.slice(0, -1))
                        : item.bio;
                    item.skillsList = item.skillsList ? item.skillsList : "";
                    item.serviceList = item.serviceTypeList
                        ? item.serviceTypeList
                        : "";
                    item.latestScheduleUpdatedAt =
                        scheduleDatesToReport[item.id] &&
                        scheduleDatesToReport[item.id].latestScheduleUpdatedAt
                            ? moment(
                                  scheduleDatesToReport[item.id]
                                      .latestScheduleUpdatedAt
                              ).format("YYYY-MM-DD hh:mm A")
                            : "";
                    rows.push(item);
                });
            }
            const csvData = rows.map((item) => {
                const {
                    status: ACTIVE,
                    id: ID,
                    firstname: FIRSTNAME,
                    lastname: LASTNAME,
                    emailaddress: EMAIL,
                    phone: PHONENUMBER,
                    permalink: PERMALINK,
                    role: ROLE,
                    timezone: TiMEZONE,
                    inhomeservicestatus: INHOMESERVICESTATUS,
                    maxTravelTime: MAXTRAVELTIME,
                    virtualservicestatus: VIRTUALSERVICESTATUS,
                    virtualMeetingLink: VIRTUALMEETINGLINK,
                    numberofschedules: SCHEDULECOUNT,
                    latestScheduleUpdatedAt: LATESTSCHEDULEUPDATEDAT,
                    contactconsent: CONTACTCONSENT,
                    skillsList: SKILLS,
                    serviceList: SERVICES,
                    bio: BIO,
                    lastLogin: LASTLOGIN,
                    createdAt: DATECREATED,
                    updatedAt: DATEUPDATED
                } = item;
                const newItem = {
                    Status: ACTIVE,
                    ID: ID,
                    "First Name": FIRSTNAME,
                    "Last Name": LASTNAME,
                    Email: EMAIL,
                    "Phone Number": PHONENUMBER,
                    Permalink: PERMALINK,
                    Role: ROLE,
                    Timezone: TiMEZONE,
                    "Available For In Home Appointments": INHOMESERVICESTATUS,
                    "Max Travel Time": MAXTRAVELTIME,
                    "Available For Virtual Appointments": VIRTUALSERVICESTATUS,
                    "Virtual Meeting Link": VIRTUALMEETINGLINK,
                    Schedules: SCHEDULECOUNT,
                    "Schedules Last Updated": LATESTSCHEDULEUPDATEDAT,
                    "Send Daily Agenda Email": CONTACTCONSENT,
                    Skills: SKILLS,
                    Services: SERVICES,
                    Bio: BIO,
                    "Last login": LASTLOGIN,
                    "Created On": DATECREATED,
                    "Updated On": DATEUPDATED
                };
                return newItem;
            });

            if (csvData && csvData.length) {
                return csvData;
            }
        } catch (e) {
            console.log(e);
        }
    };

    const prepareExportData = async () => {
        const providersToExport = await execReadByPK({
            opname: "providerByCompany",
            op: providerByCompany,
            id: { companyId: mbxUser.company.id },
            limit: process.env.REACT_APP_LISTLIMIT,
            sortDirection: "ASC"
        });

        if (providersToExport.items?.length > 0) {
            const providersCSVData = await csvData({
                items: providersToExport.items
            });
            setExportReadyProviders(providersCSVData);
            setExportDownloadReady(true);
            if (csvLinkRef.current) {
                csvLinkRef.current.link.click();
            }
        }
        setPreparingExportData(false);
    };

    useEffect(() => {
        // Auto start the export process when the component is loaded
        prepareExportData();

        const dialogTimeout = setTimeout(() => {
            if (preparingExportData) {
                setLongReport(true);
                setShowDialog(true);
            }
        }, 5000);

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                onExportComplete();
            }
        };

        // Add event listener when the component mounts
        window.addEventListener("keydown", handleEscape);

        // Clean up event listener when the component unmounts or showExport changes
        return () => {
            clearTimeout(dialogTimeout);
            window.removeEventListener("keydown", handleEscape);
        };
    }, []);

    useEffect(() => {
        if (!preparingExportData && !showDialog) {
            onExportComplete();
        }
    }, [preparingExportData, showDialog]);

    return (
        <>
            <Backdrop
                open={prepareExportData && !longReport}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>

            {exportDownloadReady && (
                <CSVLink
                    ref={csvLinkRef}
                    data={exportReadyProviders || ""}
                    headers={csvHeaders}
                    filename={`Users_${moment(Date.now()).format(
                        "YYYY-MM-DD"
                    )}.csv`}
                />
            )}

            {showDialog && (
                <LargeReportDialog
                    open={showDialog}
                    onClose={() => {
                        setShowDialog(false);
                    }}
                />
            )}
        </>
    );
};

export default ExportUsersList;
